import { Link } from "react-router-dom";
import { Badge } from "@volca/ui/components/ui/badge";
import { Button } from "@volca/ui/components/ui/button";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { formatDateAndTime } from "@/core/utils/date";
import { MetaList } from "@/profitability/meta-list";
import { useReportBuilds } from "@/profitability/report-query";
import { profitabilityRunPath } from "@/profitability/routes";
import { OUTCOME, produced } from "./outcome";

/**
 * Every build of the template, newest first: who asked, when, how it ended,
 * what it produced, and its sample run to open.
 */
export const BuildHistory = ({
  businessId,
  reportId,
  buildStatus,
  titled = true,
}: {
  businessId: string;
  reportId: string;
  buildStatus: string | null;
  /** Shows its own "Builds" heading; off where a section already names it. */
  titled?: boolean;
}) => {
  const builds = useReportBuilds(businessId, reportId, buildStatus);

  return (
    <Stack
      gap={2}
      align="stretch"
      className="w-full"
      data-testid="build-history"
    >
      {titled && (
        <Text render={<h2 />} variant="heading">
          Builds
        </Text>
      )}
      {builds.isPending ? (
        <Spinner layout="centered" aria-label="Loading builds" />
      ) : builds.isError ? (
        <Stack align="start" gap={2}>
          <Text tone="destructive" role="alert">
            Couldn&apos;t load this agent&apos;s builds. Please try again.
          </Text>
          <Button
            variant="secondary"
            loading={builds.isFetching}
            onClick={() => void builds.refetch()}
          >
            Try again
          </Button>
        </Stack>
      ) : !builds.data.length ? (
        <Text tone="muted">
          No builds yet. Each build is listed here with how it ended.
        </Text>
      ) : (
        <Stack gap={0} align="stretch" className="w-full">
          {builds.data.map((build, index) => (
            <Stack
              key={build.id}
              gap={2}
              align="stretch"
              className="min-w-0 border-b border-line py-3 last:border-b-0"
              data-testid="build-history-row"
            >
              <Stack
                direction="row"
                gap={3}
                align="center"
                className="flex-wrap"
              >
                <Badge
                  variant="secondary"
                  color={OUTCOME[build.status].color}
                  size="sm"
                >
                  {OUTCOME[build.status].label}
                </Badge>
                <Text>
                  <MetaList>
                    {formatDateAndTime(build.started_at)}
                    {build.requester.full_name}
                    {produced(build).map((part) => (
                      <span key={part} className="text-subtle">
                        {part}
                      </span>
                    ))}
                  </MetaList>
                </Text>
                {build.sample_run_id && (
                  <Link
                    to={profitabilityRunPath(build.sample_run_id)}
                    className="inline-flex items-center text-link pointer-coarse:min-h-11"
                  >
                    <Text tone="link">View sample report</Text>
                  </Link>
                )}
              </Stack>
              {/* The newest build's notes are the Build notes above, and a
                  replaced build's note only repeats its badge. */}
              {build.notes && index > 0 && build.status !== "superseded" && (
                <Text
                  tone="muted"
                  className="line-clamp-2 break-words whitespace-pre-line"
                >
                  {build.notes}
                </Text>
              )}
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
};
